import { memo, useMemo } from "react";

import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";

import InfiniteScrollWrapper from "../../../../../../common/components/infinite-scroll/InfiniteScrollWrapper";
import FullPageLoader from "../../../../../../common/components/item/FullPageLoader";
import CircularLoading from "../../../../../../common/components/progress/CircularLoading";
import TableNoDataOverlay from "../../../../../../common/components/table/TableNoDataOverlay";
import useWindowDimensions from "../../../../../../common/hooks/useWindowDimensions";
import { AutocompleteOptionType } from "../../../../../../common/models/autocomplete";
import {
  useSelectedDataHubLocation,
  useSelectedDataHubTagLocations,
} from "../../../dataHub.variable";
import { useGetLiveViewData } from "../../hooks/useGetLiveViewData";
import DeviceImageListItem from "./DeviceImageListItem";

const DevicesImageList = (): JSX.Element => {
  const theme = useTheme();
  const { width } = useWindowDimensions();

  const locationVariable = useSelectedDataHubLocation();
  const tagLocationVariable = useSelectedDataHubTagLocations();

  const { devices, loading, loadMoreDevices, nextToken, fetchMoreLoading } =
    useGetLiveViewData();

  const memoizedCards = useMemo(() => {
    let cards = [...devices];

    if (tagLocationVariable?.length) {
      cards = devices?.filter(item => {
        const nameOfLocation = item.locationId;

        const locations = tagLocationVariable.map(
          (tag: AutocompleteOptionType) => {
            if (nameOfLocation.includes("#L#")) {
              return tag?.value?.toLowerCase() ?? "";
            }

            return tag?.title?.toLowerCase() ?? "";
          }
        );

        const condition = locations.includes(nameOfLocation.toLowerCase());

        return condition;
      });
    }

    return cards;
  }, [devices]);

  const cardWidth = width < theme.breakpoints.values.xxl ? "294px" : "386px";

  if (loading && !fetchMoreLoading) {
    return <FullPageLoader />;
  }

  if (!memoizedCards?.length && !loading && !fetchMoreLoading) {
    return <TableNoDataOverlay />;
  }

  if (
    !locationVariable &&
    tagLocationVariable &&
    tagLocationVariable.length === 0
  ) {
    return <TableNoDataOverlay />;
  }

  return (
    <Box
      sx={{
        display: "grid",
        gap: "1.25em",
        gridTemplateColumns: `repeat(auto-fill, minmax(min(${cardWidth}, 100%), 1fr))`,
      }}
      component="div"
    >
      <InfiniteScrollWrapper
        fetchNextPage={loadMoreDevices}
        lastEvaluatedKey={nextToken}
        loading={loading || fetchMoreLoading}
      >
        {memoizedCards &&
          memoizedCards.map((item): JSX.Element => {
            return <DeviceImageListItem key={item.id} device={item} />;
          })}
        {fetchMoreLoading && <CircularLoading />}
      </InfiniteScrollWrapper>
    </Box>
  );
};

export default memo(DevicesImageList);
